'use client';

import heroBackground from '@/public/home/heroBackground.png';
import Image from 'next/image';
import { useRef } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton } from '@/sanity/lib/definitions';
import TrustChip from '@/components/global/TrustChip';
import { CTAButton } from '@/shadcn/ui/button';
import ChipText from '@/components/global/ChipText';
import heroCard from '@/public/home/heroCard.png';
import manInTheOffice from '@/public/home/challengeImage.png';
import challengeImageMobile from '@/public/home/challengeImageMobile.png';
import greenShape from '@/public/home/greenShape.svg';

interface IChallengeProps {
  items: {
    title: string;
    text: string;
  }[];
  chipText: string;
  title: PortableTextBlock[];
  button: GROQButton;
}

export default function Challenge({
  items,
  chipText,
  title,
  button,
}: IChallengeProps) {
  const isMobile = useIsMobile();

  const getBackgroundClass = (index: number, totalLength: number) => {
    // Last transparent
    if (index === totalLength - 1) {
      return 'bg-transparent';
    }

    if (isMobile) {
      const mobileColors = [
        'bg-[#DAF4D5]',
        'bg-[#CFF4CC]',
        'bg-[#C3F4C5]',
        'bg-[#BAF4BE]',
        'bg-[#B0F4B7]',
      ];
      return mobileColors[index] || 'bg-green/40';
    }

    // Desktop
    const groupIndex = Math.floor(index / 2) % 3;

    switch(groupIndex) {
      case 0:
        return 'bg-green/40';
      case 1:
        return 'bg-green/[0.72]';
      case 2:
        return 'bg-green';
      default:
        return 'bg-green/40';
    }
  };

  return (
    <section className="w-full flex flex-col items-center py-[15rem] lg:py-0">
      <div className="container flex flex-col lg:flex-row gap-[8rem] lg:gap-[2.5rem] lg:min-h-[155.75rem]">
        <div className="lg:sticky lg:top-0 w-full lg:max-w-[67.75rem] lg:h-[90.625rem] flex flex-col lg:pt-[12rem] flex-shrink-0">
          {chipText &&
            <ChipText text={chipText} color="purple" className="mb-[2rem]" />
          }
          <div className="flex flex-col gap-[2.62rem] prose-headings:leading-[120%] prose-headings:mb-0 prose-p:text-[2rem] lg:prose-p:text-[2.5rem] prose-p:leading-[140%] prose-p:opacity-[64%]">
            <PortableText value={title} components={richTextComponent} />
          </div>
          <CTAButton className="mt-[6.38rem] w-fit hidden lg:block" data={button} />
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-x-[3rem] gap-[11.62rem] lg:gap-y-[3.2rem] w-full lg:my-auto">
          {items?.length > 0 && items?.map((item, index) => {
            if (index === items.length - 1) {
              return (
                <div
                  key={index}
                  className="hidden lg:flex flex-col gap-[2rem] w-full lg:px-[4.62rem] lg:pt-[4.92rem] bg-transparent lg:h-[36.375rem] canto-chanfrado"
                >
                  <p className="text-[4rem] font-[400] opacity-[64%] my-auto text-fakeblack leading-[120%] mb-0">{item.title}</p>
                </div>
              );
            }

            return (
              <div key={index} className="sticky lg:static" style={{ top: `${2 + index * 3}rem` }}>
                <div className={`flex flex-col gap-[2rem] w-full lg:px-[4.62rem] lg:pt-[4.92rem] ${getBackgroundClass(index, items.length)} h-[36.375rem] p-[3rem] canto-chanfrado`}>
                  <p className="text-[3rem] font-[500] text-fakeblack leading-[120%] mb-0">{item.title}</p>
                  <p className="text-[2rem] lg:text-[2.5rem] font-[400] text-fakeblack/[64%] leading-[140%] max-w-[53.5rem] mb-0">{item.text}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-[4rem] items-center justify-center lg:hidden">
          <p className="opacity-[64%] text-[2.5rem] font-[400] text-fakeblack leading-[140%] mb-0">{items?.length > 0 && items?.[items.length - 1]?.title}</p>
          <CTAButton data={button} className="w-full" />
        </div>
      </div>
    </section>
  );
}
